import type {JSX} from 'react';

import {$convertToMarkdownString} from '@lexical/markdown';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import * as React from 'react';

import {PLAYGROUND_TRANSFORMERS} from '../MarkdownTransformers';

/**
 * GetMarkdownContentPlugin
 * 
 * A Lexical plugin that provides a function to get the current editor content as markdown.
 * 
 * Features:
 * - Uses PLAYGROUND_TRANSFORMERS for enhanced markdown support (beyond standard TRANSFORMERS)
 * - Exports all custom nodes: Images, Tweets, Equations, Tables, etc.
 * - Real-time content extraction
 * - Configurable newline preservation
 */
export default function GetMarkdownContentPlugin({
  onGetContent,
  shouldPreserveNewLinesInMarkdown = false,
}: {
  onGetContent: React.MutableRefObject<() => string>;
  shouldPreserveNewLinesInMarkdown?: boolean;
}): JSX.Element {
  const [editor] = useLexicalComposerContext();

  React.useEffect(() => {
    const getContent = (): string => {
      let markdown = '';
      
      try {
        editor.getEditorState().read(() => {
          // This provides export support for:
          // - Standard markdown (headers, lists, formatting, links, etc.)
          // - Custom nodes (images, equations, tables)
          markdown = $convertToMarkdownString(
            PLAYGROUND_TRANSFORMERS,
            undefined, // node (undefined means use root)
            shouldPreserveNewLinesInMarkdown,
          );
          
          markdown = markdown.replace(/\n\n/g, '\n'); // Normalize newlines       
        });
      } catch (error) {
        console.warn('Markdown export failed:', error);
        // Return empty string if export fails
        markdown = '';
      }
      
      return markdown;
    };

    // Assign the getContent function to the ref
    onGetContent.current = getContent;
  }, [editor, onGetContent, shouldPreserveNewLinesInMarkdown]);

  return <></>;
}
